<template>
  <section class="w-full h-full">
    <!--=====================================
    =         Premium                       =
    ======================================-->
    <Premium @triggerFaq="triggerFaq" />
    <!-- =============================================
    =           Testimonials and FAQ              =
    ============================================= -->
    <div class="px-2">
      <div class="max-w-1200 mx-auto my-6 flex flex-col xl:flex-row">
        <!-- Testimonial Carousel -->
        <carousel v-model="activeTestimony" class="w-full bg-white">
          <div
            v-for="(testimony, i) in testimonials"
            :key="i"
            class="ds-text-2xl font-bold text-gray-1000 bg-white w-full rounded-lg overflow-hidden p-4 relative flex flex-col"
          >
            <div class="flex-1">
              <img
                src="https://cdn.sportsrecruits.com/assets/itg/quote-mark.svg"
                class="absolute left-0 top-0 w-24 h-24"
              />

              <p
                class="ds-text-3xl md:ds-text-4xl text-gray-1000"
                style="margin-top: 5rem"
                v-html="testimony.content"
              ></p>
            </div>
            <div class="flex flex-col w-3/4 sm:w-full">
              <div class="flex items-center mt-6 mb-4">
                <!-- <UserAvatar :size="64" :image-url="testimony.user.avatar" /> -->

                <div
                  :style="`background: url(${testimony.user.avatar}) no-repeat; background-size: cover; background-position: top center;`"
                  class="rounded-full shadow h-16 w-16 border border-solid border-gray-100 z-10 bg-white"
                />
                <div
                  :style="`background: url(${testimony.college.logo}) no-repeat; background-size: 48px 48px; background-position: center center;`"
                  class="rounded-full shadow h-16 w-16 border border-solid border-gray-100 z-10 -ml-2 bg-white"
                />
              </div>

              <p class="text-gray-900 text-sm ds-text-sm font-semibold">
                {{ testimony.user.title }}
              </p>

              <p class="text-gray-900 text-sm ds-text-sm font-medium">
                Committed to {{ testimony.college.name }}, {{ testimony.sport }}
              </p>
            </div>
          </div>
        </carousel>
      </div>
    </div>
    <div class="w-full bg-gray-1000 pb-16">
      <div class="max-w-1200 mx-auto">
        <div class="text-white px-4 py-8">
          <p class="text-white text-3xl ds-text-3xl font-bold text-center mb-6">
            Frequently Asked Questions
          </p>

          <accordion
            v-model="activeFaq"
            :sections="faqs"
            class="divide-solid divide-y divide-white mb-6 mx-auto"
            header-class="first:border-t last:border-b border-l-0 border-r-0 border-white"
            style="max-width: 768px"
            @sectionOpened="trackFaq"
          ></accordion>
        </div>
      </div>
      <div class="max-w-1200 px-3 mx-auto text-white font-bold ds-text-2xl text-center">
        <p>If you have any other questions, please reach out to <TextLink link="mailto:help@sportsrecruits.com" text="help@sportsrecruits.com" /></p>
      </div>
    </div>
  </section>
</template>

<script>
import tracking from "./utils/tracking";
import Accordion from "@/components/Pricing/components/Accordion"; // Located in SR Pricing Directory
import Carousel from "@/components/Pricing/components/Carousel"; // Located in SR Pricing Directory
import { Button, TextLink } from "@sportsrecruits/playbook";
import Premium from "./Premium/PricingPremium";
import { mapState } from "vuex";

export default {
  name: "Pricing",
  components: {
    Carousel,
    Accordion,
    Button,
    Premium,
    TextLink
  },
  data() {
    return {
      activeFaq: null,
      activeTestimony: null,
      canViewInsights: true,
      hasMounted: false,
    };
  },
  computed: {
    ...mapState("pricing", {
      activeTab: (state) => state.activeTab,
      products: (state) => state.products,
      featureGroups: (state) => state.featureGroups,
      testimonials: (state) => state.testimonials,
      faqs: (state) => state.faqs,
    }),
    isMobile() {
      return ['mobile', 'xs', 'sm'].includes(this.$mq);
    }
  },
  watch: {
    activeTestimony(index) {
      let testimony = this.$store.getters["pricing/getTestimony"](index);

      if (testimony) {
        this.trackTestimony(testimony);
      }
    },
  },
  methods: {
    triggerFaq({ faq, index }) {
        let el = document.querySelector(`[data-faq-id='${faq}']`);

        if (!this.activeFaq || !this.activeFaq?.includes(index-1)) el.firstChild.click();

        let faqY = el.getBoundingClientRect().top;
        let faqPos = !this.isMobile ? faqY : faqY + 650;

        setTimeout(() => {
          window.scrollTo({
            left: 0,
            top: faqPos,
            behavior: "smooth",
          });
        }, 100);
    },
    trackFaq(index) {
      let faq = this.$store.getters["pricing/getFaq"](index);

      if (faq) {
        tracking.faq({
          event: "clicked_faq",
          cta_location: faq.label,
        });
      }
    },
    trackTestimony(testimony) {
      tracking.testimony({
        event: "clicked_testimonial_nav",
        cta_location: testimony.user.title,
      });
    },
  },
  beforeMount() {
    this.$store.commit("pricing/setProducts", window.pricing.products);
    this.$store.commit(
      "pricing/setFeatureGroups",
      window.pricing.featureGroups
    );
    this.$store.commit("pricing/setTestimonials", window.pricing.testimonials);
    this.$store.commit("pricing/setFaqs", window.pricing.faqs);
    this.$store.commit("pricing/setPlans", window.pricing.plans);

    this.canViewInsights = window.pricing.canViewInsights;
  },
};
</script>

<style scoped lang="scss">
::v-deep .max-w-1200 {
  max-width: 1200px;
}

::v-deep .custom-grid-columns {
  grid-template-columns: minmax(0px, 302px) repeat(3, minmax(0, 1fr));
}

@media screen and (max-width: 1023px) {
  ::v-deep .custom-grid-columns {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media screen and (max-width: 767px) {
  ::v-deep .custom-grid-columns {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

::v-deep .lg-header {
  font-size: 60px;
  line-height: 64px;
}

::v-deep .bg-half-white {
  background-image: linear-gradient(
    to bottom,
    white 50%,
    rgba(255, 255, 255, 0) 50%
  );
}
</style>